import React, { useState, useEffect } from 'react';
import axios from 'axios';
import type {Session} from '@supabase/supabase-js';
import './WatchList.css';
import {supabase} from "./supabaseClient";

const NO_POSTER = `data:image/svg+xml;utf8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="300" viewBox="0 0 200 300">' +
    '<rect width="200" height="300" fill="#2a2a33"/>' +
    '<text x="100" y="155" fill="#8a8a99" font-family="sans-serif" font-size="16" text-anchor="middle">Нет постера</text>' +
    '</svg>'
)}`;

type SortMode = 'date' | 'rating' | 'title';

// Updated to include TV series information
interface Movie {
    id: number;
    tmdbId: number;
    title: string;
    poster_path: string;
    overview: string;
    release_date: string;
    watched: boolean;
    comment?: string;
    rating?: number; // Added rating field
    addedAt: string;
    watchedAt?: string;
    mediaType: 'movie' | 'tv'; // Added to distinguish between movies and TV shows
    seasons?: Season[]; // For TV shows only
}

// New interface for TV seasons
interface Season {
    season_number: number;
    name: string;
    watched: boolean;
    comment?: string;
    rating?: number;
    watchedAt?: string;
}

function rowToMovie(row: any): Movie {
    return {
        id: row.id,
        tmdbId: row.tmdb_id,
        title: row.title,
        poster_path: row.poster_path,
        overview: row.overview,
        release_date: row.release_date,
        watched: row.watched,
        comment: row.comment ?? undefined,
        rating: row.rating ?? undefined,
        addedAt: row.added_at,
        watchedAt: row.watched_at ?? undefined,
        mediaType: row.media_type,
        seasons: row.seasons ?? undefined,
    };
}

function movieToRow(movie: Movie) {
    return {
        id: movie.id,
        tmdb_id: movie.tmdbId,
        title: movie.title,
        poster_path: movie.poster_path,
        overview: movie.overview,
        release_date: movie.release_date,
        watched: movie.watched,
        comment: movie.comment ?? null,
        rating: movie.rating ?? null,
        added_at: movie.addedAt,
        watched_at: movie.watchedAt ?? null,
        media_type: movie.mediaType,
        seasons: movie.seasons ?? null,
    };
}

const WatchList: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [tvSeasonsCache, setTvSeasonsCache] = useState<Record<number, Season[]>>({});


    const [movies, setMovies] = useState<Movie[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
    const [comment, setComment] = useState('');
    const [rating, setRating] = useState<number>(0);
    const [watchedDate, setWatchedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [expandedTvShows, setExpandedTvShows] = useState<Record<number, boolean>>({});
    const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
    const [seasonDetailsExpanded, setSeasonDetailsExpanded] = useState<Record<number, Record<number, boolean>>>({});
    const [showMovies, setShowMovies] = useState(true);
    const [showTVShows, setShowTVShows] = useState(true);
    const [overviewMovie, setOverviewMovie] = useState<Movie | null>(null);

    // New state for TV season reviews
    const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
    const [isSeason, setIsSeason] = useState(false);

    // Состояния для отображения групп
    const [showWatched, setShowWatched] = useState(true);
    const [showUnwatched, setShowUnwatched] = useState(true);
    const [listFilter, setListFilter] = useState('');
    const [sortMode, setSortMode] = useState<SortMode>('date');

    // Writes are allowed only for the signed-in owner; RLS enforces it on the server side
    const [session, setSession] = useState<Session | null>(null);
    const [showLogin, setShowLogin] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState<string | null>(null);
    const canEdit = session !== null;

    useEffect(() => {
        supabase.auth.getSession().then(({data}) => setSession(data.session));
        const {data: {subscription}} = supabase.auth.onAuthStateChange((_event, newSession) => setSession(newSession));
        return () => subscription.unsubscribe();
    }, []);

    const signIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError(null);
        const {error} = await supabase.auth.signInWithPassword({email: loginEmail, password: loginPassword});
        if (error) {
            setLoginError('Не удалось войти. Проверьте почту и пароль.');
            return;
        }
        setShowLogin(false);
        setLoginPassword('');
    };

    const signOut = async () => {
        await supabase.auth.signOut();
    };

    const TMDB_API_KEY = '3e7af10965c8c1f641da44c18b4795ad';
    const TMDB_PROXY_BASE = (process.env.REACT_APP_TMDB_PROXY_BASE as string) || '/tmdb-api';

    // Загружаем сохраненные фильмы при монтировании компонента
    useEffect(() => {
        fetchMovies();
    }, []);

    // Обновите функцию fetchMovies
    const fetchMovies = async () => {
        setIsLoading(true);
        setLoadError(null);

        try {
            const { data, error } = await supabase
                .from('watchlist_items')
                .select('*')
                .order('added_at', { ascending: true });
            if (error) throw error;
            if (data) {
                const loaded = data.map(rowToMovie);
                setMovies(loaded);
                refreshTvSeasons(loaded);
            } else {
                setMovies([]);
            }
        } catch (error) {
            console.error('Ошибка при загрузке списка фильмов:', error);
            setLoadError('Не удалось загрузить список фильмов. Пожалуйста, проверьте подключение к интернету и попробуйте еще раз.');
            setMovies([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Saves only the rows that changed, so a stale tab can no longer wipe items added elsewhere
    const saveMovie = async (updatedMovies: Movie[], changedId: number) => {
        setMovies(updatedMovies);
        const changed = updatedMovies.find(movie => movie.id === changedId);
        if (!changed) return;

        const { error } = await supabase
            .from('watchlist_items')
            .upsert(movieToRow(changed), { onConflict: 'id' });
        if (error) {
            console.error('Ошибка при сохранении:', error);
            alert('Не удалось сохранить изменения. Пожалуйста, попробуйте еще раз.');
            fetchMovies();
        }
    };

    const removeMovie = async (id: number) => {
        setMovies(prev => prev.filter(movie => movie.id !== id));
        const { error } = await supabase
            .from('watchlist_items')
            .delete()
            .eq('id', id);
        if (error) {
            console.error('Ошибка при удалении:', error);
            alert('Не удалось удалить. Пожалуйста, попробуйте еще раз.');
            fetchMovies();
        }
    };

    // Picks up seasons released since a show was added; a new season makes the show unwatched again
    const refreshTvSeasons = async (loaded: Movie[]) => {
        const tvShows = loaded.filter(movie => movie.mediaType === 'tv' && movie.seasons?.length);
        const updates = await Promise.all(tvShows.map(async (movie) => {
            const latestSeasons = await fetchTVSeasons(movie.tmdbId) as { season_number: number; name: string }[];
            const known = new Set(movie.seasons!.map(s => s.season_number));
            const added = latestSeasons
                .filter(s => !known.has(s.season_number) && s.name !== "Спецматериалы")
                .map(s => ({ season_number: s.season_number, name: s.name, watched: false }));
            if (added.length === 0) return null;
            return {
                ...movie,
                seasons: [...movie.seasons!, ...added],
                watched: false,
                watchedAt: undefined,
                comment: undefined,
            } as Movie;
        }));

        const changed = updates.filter((movie): movie is Movie => movie !== null);
        if (changed.length === 0) return;

        const byId = new Map(changed.map(movie => [movie.id, movie]));
        setMovies(prev => prev.map(movie => byId.get(movie.id) ?? movie));

        const { data } = await supabase.auth.getSession();
        if (data.session) {
            await supabase.from('watchlist_items').upsert(changed.map(movieToRow), { onConflict: 'id' });
        }
    };

    // Поиск фильмов и сериалов через TMDB API
    const searchMovies = async () => {
        if (!searchQuery.trim()) return;

        setLoading(true);
        try {
            const response = await axios.get(
                `${TMDB_PROXY_BASE}/3/search/multi?include_adult=true&api_key=${TMDB_API_KEY}&query=${encodeURIComponent(searchQuery)}&language=ru-RU`
            );
            // Фильтруем результаты, чтобы включать только фильмы и сериалы
            const filteredResults = response.data.results.filter(
                (item: any) => item.media_type === 'movie' || item.media_type === 'tv'
            );
            setSearchResults(filteredResults);
        } catch (error) {
            console.error('Ошибка при поиске:', error);
            alert('Не удалось выполнить поиск. Пожалуйста, попробуйте еще раз.');
        } finally {
            setLoading(false);
        }
    };

    // Получение информации о сезонах сериала
    const fetchTVSeasons = async (tvId: number) => {
        try {
            const response = await axios.get(
                `${TMDB_PROXY_BASE}/3/tv/${tvId}?api_key=${TMDB_API_KEY}&language=ru-RU`
            );
            return response.data.seasons;
        } catch (error) {
            console.error('Ошибка при получении информации о сезонах:', error);
            return [];
        }
    };

    // Добавление фильма или сериала в вотчлист
    const addMovie = async (tmdbMovie: any) => {
        const mediaType = tmdbMovie.media_type;

        let seasons: Season[] = [];
        if (mediaType === 'tv') {
            const tvSeasons = await fetchTVSeasons(tmdbMovie.id);
            seasons = tvSeasons.map((season: any) => ({
                season_number: season.season_number,
                name: season.name,
                watched: false
            }));
        }

        seasons = seasons.filter(season => season.name !== "Спецматериалы");

        const newMovie: Movie = {
            id: Date.now(), // Уникальный ID для нашей системы
            tmdbId: tmdbMovie.id,
            title: tmdbMovie.title || tmdbMovie.name,
            poster_path: tmdbMovie.poster_path,
            overview: tmdbMovie.overview,
            release_date: tmdbMovie.release_date || tmdbMovie.first_air_date,
            watched: false,
            addedAt: new Date().toISOString(),
            mediaType: mediaType,
            ...(mediaType === 'tv' && { seasons })
        };

        await saveMovie([...movies, newMovie], newMovie.id);
        setSearchResults([]);
        setSearchQuery('');
    };

    // Удаление фильма из вотчлиста
    const deleteMovie = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить этот фильм из списка?')) {
            await removeMovie(id);
        }
    };

    // Отметка фильма как просмотренного
    const markAsWatched = async (id: number) => {
        const movieToUpdate = movies.find(movie => movie.id === id);
        if (!movieToUpdate) return;

        setSelectedMovie(movieToUpdate);
        setIsSeason(false);

        if (movieToUpdate.watchedAt) {
            setWatchedDate(new Date(movieToUpdate.watchedAt).toISOString().split('T')[0]);
        } else {
            setWatchedDate(new Date().toISOString().split('T')[0]);
        }

        setRating(movieToUpdate.rating || 0);
        setComment(movieToUpdate.comment || '');

        if (movieToUpdate.watched) {
            // Если фильм уже отмечен как просмотренный, просто переключаем обратно
            const updatedMovies = movies.map(movie =>
                movie.id === id ? { ...movie, watched: false, comment: undefined, rating: undefined, watchedAt: undefined } : movie
            );
            await saveMovie(updatedMovies, id);
        }
    };

    // Отметка сезона сериала как просмотренного
    const markSeasonAsWatched = (movieId: number, seasonNumber: number, openEditor = true) => {
        const movieToUpdate = movies.find(movie => movie.id === movieId);
        if (!movieToUpdate || !movieToUpdate.seasons) return;

        const season = movieToUpdate.seasons.find(s => s.season_number === seasonNumber);
        if (!season) return;

        // Если сезон уже просмотрен и мы не хотим открывать модалку — просто снимаем флаг
        if (season.watched && !openEditor) {
            const updatedSeasons = movieToUpdate.seasons.map(s =>
                s.season_number === seasonNumber
                    ? { ...s, watched: false, comment: undefined, rating: undefined, watchedAt: undefined }
                    : s
            );

            const allSeasonsWatched = updatedSeasons.every(s => s.watched);
            const lastSeason = movieToUpdate.seasons?.[movieToUpdate.seasons.length - 1];

            const updatedMovies = movies.map(movie =>
                movie.id === movieId
                    ? {
                        ...movie,
                        seasons: updatedSeasons,
                        watched: allSeasonsWatched,
                        ...(allSeasonsWatched
                            ? {
                                watchedAt: lastSeason?.watchedAt,
                                comment: 'Все сезоны просмотрены'
                            }
                            : {
                                watchedAt: undefined,
                                comment: undefined
                            })
                    }
                    : movie
            );

            saveMovie(updatedMovies, movieId);
            return;
        }

        // Иначе — открываем модалку для редактирования/добавления
        setSelectedMovie(movieToUpdate);
        setSelectedSeason(seasonNumber);
        setIsSeason(true);

        if (season.watchedAt) {
            setWatchedDate(new Date(season.watchedAt).toISOString().split('T')[0]);
        } else {
            setWatchedDate(new Date().toISOString().split('T')[0]);
        }

        setRating(season.rating || 0);
        setComment(season.comment || '');
    };


    // Сохранение комментария к просмотренному фильму или сезону
    const saveComment = async () => {
        if (!selectedMovie) return;

        if (isSeason && selectedSeason !== null && selectedMovie.seasons) {
            // Сохраняем комментарий для сезона сериала
            const updatedSeasons = selectedMovie.seasons.map(season =>
                season.season_number === selectedSeason ?
                    { ...season, watched: true, comment, rating, watchedAt: new Date(watchedDate).toISOString() } :
                    season
            );

            // Проверяем, все ли сезоны просмотрены
            const allSeasonsWatched = updatedSeasons.every(season => season.watched);

            const updatedMovies = movies.map(movie =>
                movie.id === selectedMovie.id ?
                    {
                        ...movie,
                        seasons: updatedSeasons,
                        watched: allSeasonsWatched, // Помечаем весь сериал как просмотренный, если все сезоны просмотрены
                        ...(allSeasonsWatched && {
                            watchedAt: updatedSeasons
                                .filter(s => s.watchedAt)
                                .map(s => new Date(s.watchedAt!))
                                .sort((a, b) => b.getTime() - a.getTime())[0]?.toISOString(),
                            comment: 'Все сезоны просмотрены'
                        })
                    } :
                    movie
            );

            await saveMovie(updatedMovies, selectedMovie.id);
        } else {
            // Сохраняем комментарий для фильма
            const updatedMovies = movies.map(movie =>
                movie.id === selectedMovie.id ?
                    {
                        ...movie,
                        watched: true,
                        comment,
                        rating,
                        watchedAt: new Date(watchedDate).toISOString()
                    } :
                    movie
            );

            await saveMovie(updatedMovies, selectedMovie.id);
        }

        setSelectedMovie(null);
        setSelectedSeason(null);
        setIsSeason(false);
        setComment('');
        setRating(0);
        setWatchedDate(new Date().toISOString().split('T')[0]);
    };

    const toggleSeasonsList = async (movieId: number) => {
        setExpandedTvShows(prev => ({
            ...prev,
            [movieId]: !prev[movieId]
        }));

        const movie = movies.find(m => m.id === movieId);
        if (!movie || movie.seasons?.length) return;

        // загружаем сезоны
        try {
            const latestSeasons = await fetchTVSeasons(movie.tmdbId) as { season_number: number; name: string }[];
            const filteredSeasons = latestSeasons
                .filter((season) => season.name !== "Спецматериалы")
                .map((season) => ({
                    season_number: season.season_number,
                    name: season.name,
                    watched: false
                }));


            const updatedMovies = movies.map(m =>
                m.id === movieId ? { ...m, seasons: filteredSeasons } : m
            );

            setMovies(updatedMovies);
        } catch (e) {
            console.error('Ошибка при загрузке сезонов:', e);
        }
    };



    const toggleSeasonDetails = (movieId: number, seasonNumber: number) => {
        setSeasonDetailsExpanded(prev => ({
            ...prev,
            [movieId]: {
                ...prev[movieId],
                [seasonNumber]: !prev[movieId]?.[seasonNumber]
            }
        }));
    };

    const toggleComment = (movieId: number) => {
        setExpandedComments(prev => ({
            ...prev,
            [movieId]: !prev[movieId]
        }));
    };

    const normalizedFilter = listFilter.trim().toLowerCase();
    const isVisible = (movie: Movie) => {
        const isRightType = (showMovies && movie.mediaType === 'movie') || (showTVShows && movie.mediaType === 'tv');
        return isRightType && (!normalizedFilter || movie.title.toLowerCase().includes(normalizedFilter));
    };

    const sortBy = (dateOf: (movie: Movie) => string | undefined) => (a: Movie, b: Movie) => {
        if (sortMode === 'title') return a.title.localeCompare(b.title, 'ru');
        if (sortMode === 'rating' && (a.rating || 0) !== (b.rating || 0)) return (b.rating || 0) - (a.rating || 0);
        return new Date(dateOf(b) || "").getTime() - new Date(dateOf(a) || "").getTime();
    };

    const watchedMovies = movies
        .filter(movie => movie.watched && isVisible(movie))
        .sort(sortBy(movie => movie.watchedAt));

    const unwatchedMovies = movies
        .filter(movie => !movie.watched && isVisible(movie))
        .sort(sortBy(movie => movie.addedAt));

    const ratedItems = [
        ...movies.filter(movie => movie.mediaType === 'movie' && movie.watched && movie.rating),
        ...movies.flatMap(movie => movie.seasons || []).filter(season => season.watched && season.rating),
    ];
    const stats = {
        movies: movies.filter(movie => movie.mediaType === 'movie' && movie.watched).length,
        seasons: movies.flatMap(movie => movie.seasons || []).filter(season => season.watched).length,
        planned: movies.filter(movie => !movie.watched).length,
        avgRating: ratedItems.length
            ? (ratedItems.reduce((sum, item) => sum + (item.rating || 0), 0) / ratedItems.length).toFixed(1)
            : '—',
    };

    // Переключение отображения групп
    const toggleWatched = () => {
        setShowWatched(!showWatched);
    };

    const toggleUnwatched = () => {
        setShowUnwatched(!showUnwatched);
    };

    // Компонент для рейтинга звездами
    const StarRating = ({ totalStars = 10, selectedRating = 0, onRatingChange }: {
        totalStars?: number;
        selectedRating?: number;
        onRatingChange: (rating: number) => void;
    }) => {
        return (
            <div className="star-rating">
                {[...Array(totalStars)].map((_, index) => {
                    const ratingValue = index + 1;
                    return (
                        <span
                            key={index}
                            className={`star ${ratingValue <= selectedRating ? 'filled' : 'empty'}`}
                            onClick={() => onRatingChange(ratingValue)}
                        >
                            ★
                        </span>
                    );
                })}
                <span className="rating-value">{selectedRating}/10</span>
            </div>
        );
    };

    // Функция для форматирования даты в локальном формате
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className={`watchlist-container${canEdit ? '' : ' watchlist-readonly'}`}>
            <header className="App-header">
                <p>WATCHLIST</p>
                <div className="auth-bar">
                    {canEdit ? (
                        <button className="auth-btn" onClick={signOut}>Выйти</button>
                    ) : (
                        <button className="auth-btn" onClick={() => setShowLogin(true)}>Войти</button>
                    )}
                </div>
            </header>

            <div className="watchlist-stats">
                <div><strong>{stats.movies}</strong><span>фильмов посмотрено</span></div>
                <div><strong>{stats.seasons}</strong><span>сезонов посмотрено</span></div>
                <div><strong>{stats.planned}</strong><span>в планах</span></div>
                <div><strong>{stats.avgRating}</strong><span>средняя оценка</span></div>
            </div>

            {showLogin && (
                <div className="modal-overlay" onClick={() => setShowLogin(false)}>
                    <form className="comment-modal login-modal" onClick={(e) => e.stopPropagation()} onSubmit={signIn}>
                        <h2>Вход</h2>
                        <input type="email" placeholder="Почта" value={loginEmail} autoComplete="username"
                               onChange={(e) => setLoginEmail(e.target.value)}/>
                        <input type="password" placeholder="Пароль" value={loginPassword}
                               autoComplete="current-password" onChange={(e) => setLoginPassword(e.target.value)}/>
                        {loginError && <p className="login-error">{loginError}</p>}
                        <div className="modal-actions">
                            <button type="submit">Войти</button>
                            <button type="button" onClick={() => setShowLogin(false)}>Отмена</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="search-section">
                <h2>Добавить новый фильм или сериал</h2>
                <div className="search-box">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Введите название фильма или сериала"
                        onKeyDown={(e) => e.key === 'Enter' && searchMovies()}
                    />
                    <button onClick={searchMovies} disabled={loading}>
                        {loading ? 'Поиск...' : 'Найти'}
                    </button>
                </div>

                {searchResults.length > 0 && (
                    <div className="search-results">
                        <h3>Результаты поиска</h3>
                        <div className="results-grid">
                            {searchResults.map(item => (
                                <div key={item.id} className="movie-card search-result">
                                    <img
                                        loading="lazy"
                                        src={item.poster_path ? `/tmdb-image/t/p/w200${item.poster_path}` : NO_POSTER}
                                        alt={item.title || item.name}
                                    />
                                    <div className="movie-info">
                                        <h3>{item.title || item.name}</h3>
                                        <p className="release-date">
                                            {(item.release_date || item.first_air_date) &&
                                                new Date(item.release_date || item.first_air_date).getFullYear()}
                                        </p>
                                        <p className="media-type">
                                            {item.media_type === 'movie' ? 'Фильм' : 'Сериал'}
                                        </p>
                                        <button onClick={() => addMovie(item)}>Добавить в список</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isLoading && (
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Загрузка данных...</p>
                </div>
            )}

            {loadError && (
                <div className="error-message">
                    <p>{loadError}</p>
                    <button onClick={fetchMovies}>Попробовать снова</button>
                </div>
            )}

            <div className="movies-grid">
                {/*<h2>Мой список</h2>*/}

                <div className="list-toolbar">
                    <div className="filter-pills">
                        <button className={`filter-pill${showMovies ? ' filter-pill--active' : ''}`}
                                onClick={() => setShowMovies(!showMovies)}>
                            Фильмы
                        </button>
                        <button className={`filter-pill${showTVShows ? ' filter-pill--active' : ''}`}
                                onClick={() => setShowTVShows(!showTVShows)}>
                            Сериалы
                        </button>
                    </div>
                    <input
                        className="list-filter"
                        type="search"
                        value={listFilter}
                        onChange={(e) => setListFilter(e.target.value)}
                        placeholder="Поиск по списку"
                    />
                    <select className="list-sort" value={sortMode}
                            onChange={(e) => setSortMode(e.target.value as SortMode)}>
                        <option value="date">Сначала новые</option>
                        <option value="rating">По оценке</option>
                        <option value="title">По названию</option>
                    </select>
                </div>

                {movies.length === 0 ? (
                    <p className="empty-list">Ваш список пуст. Добавьте фильмы или сериалы, используя поиск выше.</p>
                ) : (
                    <>
                        {/* Секция непросмотренных фильмов и сериалов */}
                        <div className="movie-group">
                            <div className="group-header" onClick={toggleUnwatched}>
                                <h3>Ожидают просмотра ({unwatchedMovies.length})</h3>
                                <div className={`toggle-icon ${showUnwatched ? 'open' : 'closed'}`}>
                                    {showUnwatched ? '▼' : '►'}
                                </div>
                            </div>

                            {showUnwatched && unwatchedMovies.length > 0 && (
                                <div className="grid">
                                    {unwatchedMovies.map(movie => (
                                        <div key={movie.id} className="movie-card">
                                            <button className="desc-btn"
                                                    onClick={() => setOverviewMovie(movie)}>Описание
                                            </button>
                                            <div className="poster-container">

                                                <img onClick={() => setOverviewMovie(movie)}
                                                     loading="lazy"
                                                     src={movie.poster_path ? `/tmdb-image/t/p/original${movie.poster_path}` : NO_POSTER}
                                                     alt={movie.title}
                                                />
                                                <div className="media-type-badge">
                                                    {movie.mediaType === 'movie' ? 'Фильм' : 'Сериал'}
                                                </div>
                                            </div>
                                            <div className="movie-info">
                                                <h3>{movie.title}</h3>

                                                <p className="release-date">
                                                    {movie.release_date && new Date(movie.release_date).getFullYear()}
                                                </p>
                                                <p className="added-date">
                                                    Добавлено: {formatDate(movie.addedAt)}
                                                </p>

                                                {movie.mediaType === 'movie' ? (
                                                    <div className="movie-actions">
                                                        <button
                                                            onClick={() => markAsWatched(movie.id)}
                                                            className="watched-btn"
                                                            aria-label="Отметить просмотренным"
                                                        >
                                                            ✅
                                                        </button>
                                                        <button
                                                            onClick={() => deleteMovie(movie.id)}
                                                            className="delete-btn"
                                                            aria-label="Удалить"
                                                        >
                                                            ❌
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="tv-seasons">
                                                        <div className="seasons-header"
                                                             onClick={() => toggleSeasonsList(movie.id)}>
                                                            <h4>Сезоны:</h4>
                                                            <div
                                                                className={`toggle-icon ${expandedTvShows[movie.id] ? 'open' : 'closed'}`}>
                                                                {expandedTvShows[movie.id] ? '▼' : '►'}
                                                            </div>
                                                        </div>

                                                        {expandedTvShows[movie.id] && (
                                                            <ul className="seasons-list">
                                                                {movie.seasons?.map(season => (
                                                                    <li key={season.season_number}
                                                                        className="season-item">
                                                                        <div
                                                                            className="comment-header"
                                                                            onClick={() => toggleSeasonDetails(movie.id, season.season_number)}
                                                                        >

                                                                            {season.watched ? (
                                                                                <div className="season-buttons">
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            markSeasonAsWatched(movie.id, season.season_number)
                                                                                        }
                                                                                        className="edit-btn"
                                                                                        title="Редактировать"
                                                                                        style={{
                                                                                            width: '32px',
                                                                                            minWidth: '32px',
                                                                                            maxWidth: '32px',
                                                                                            padding: '6px',
                                                                                            flex: 'none',
                                                                                        }}
                                                                                    >
                                                                                        ✏️
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            markSeasonAsWatched(movie.id, season.season_number, false)
                                                                                        }
                                                                                        className="unwatched-btn"
                                                                                        title="Снять просмотр"
                                                                                        style={{
                                                                                            width: '32px',
                                                                                            minWidth: '32px',
                                                                                            maxWidth: '32px',
                                                                                            padding: '6px',
                                                                                            flex: 'none',
                                                                                        }}
                                                                                    >
                                                                                        ❌
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="season-buttons">
                                                                                    <button
                                                                                        onClick={() => markSeasonAsWatched(movie.id, season.season_number)}
                                                                                        className="watched-btn"
                                                                                        title="Отметить просмотренным"
                                                                                        style={{
                                                                                            marginLeft: '12px',
                                                                                            width: '32px',
                                                                                            minWidth: '32px',
                                                                                            maxWidth: '32px',
                                                                                            padding: '6px',
                                                                                            flex: 'none',
                                                                                        }}
                                                                                    >
                                                                                        ✅
                                                                                    </button>

                                                                                </div>


                                                                            )}

                                                                            <span>{season.name}</span>

                                                                            {season.watched ? <div
                                                                                className={`toggle-icon ${
                                                                                    seasonDetailsExpanded[movie.id]?.[season.season_number] ? 'open' : 'closed'
                                                                                }`}
                                                                            >
                                                                                {seasonDetailsExpanded[movie.id]?.[season.season_number] ? '▼' : '▼'}
                                                                            </div> : <></>}

                                                                        </div>


                                                                        {seasonDetailsExpanded[movie.id]?.[season.season_number] &&
                                                                            season.watched && (
                                                                                <div
                                                                                    className="season-info comment-content">
                                                                                    {season.rating && (
                                                                                        <p>
                                                                                            <strong>Оценка:</strong> {season.rating}/10
                                                                                        </p>
                                                                                    )}
                                                                                    {season.comment && (
                                                                                        <p className="season-comment">
                                                                                            <strong>Отзыв:</strong> {season.comment}
                                                                                        </p>
                                                                                    )}
                                                                                    {season.watchedAt && (
                                                                                        <p>
                                                                                            <strong>Просмотрено:</strong>{' '}
                                                                                            {formatDate(season.watchedAt)}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                    </li>


                                                                ))}
                                                            </ul>
                                                        )}

                                                        <button onClick={() => deleteMovie(movie.id)}
                                                                className="delete-btn">
                                                            ❌
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {showUnwatched && unwatchedMovies.length === 0 && (
                                <p className="empty-group">Нет непросмотренных фильмов или сериалов</p>
                            )}
                        </div>

                        {/* Секция просмотренных фильмов и сериалов */}
                        <div className="movie-group">
                            <div className="group-header" onClick={toggleWatched}>
                                <h3>Просмотрено ({watchedMovies.length})</h3>
                                <div className={`toggle-icon ${showWatched ? 'open' : 'closed'}`}>
                                    {showWatched ? '▼' : '▼'}
                                </div>
                            </div>

                            {showWatched && watchedMovies.length > 0 && (
                                <div className="grid">
                                    {watchedMovies.map(movie => (
                                        <div key={movie.id} className="movie-card watched">
                                            <button className="desc-btn-watched"
                                                    onClick={() => setOverviewMovie(movie)}>Описание
                                            </button>
                                            <div className="poster-container">
                                                <img
                                                    onClick={() => setOverviewMovie(movie)}
                                                    loading="lazy"
                                                    src={movie.poster_path ? `/tmdb-image/t/p/original${movie.poster_path}` : NO_POSTER}
                                                    alt={movie.title}
                                                />
                                                <div className="watched-badge">Просмотрено</div>

                                                <div className="media-type-badge">
                                                    {movie.mediaType === 'movie' ? 'Фильм' : 'Сериал'}
                                                </div>
                                            </div>
                                            <div className="movie-info">
                                                <h3>{movie.title}</h3>
                                                <p className="release-date">
                                                    {movie.release_date && new Date(movie.release_date).getFullYear()}
                                                </p>

                                                {movie.mediaType === 'movie' ? (
                                                    <>
                                                        {movie.rating && (
                                                            <div className="movie-rating">
                                                                <p>{movie.rating}/10</p>
                                                                <div className="static-stars">
                                                                    {[...Array(10)].map((_, index) => (
                                                                        <span
                                                                            key={index}
                                                                            className={`star ${index < movie.rating! ? 'filled' : 'empty'}`}
                                                                        >
                                                                            ★
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {movie.comment && (
                                                            <div className="movie-comment">
                                                                <div className="comment-header"
                                                                     onClick={() => toggleComment(movie.id)}>
                                                                    <p><strong>Отзыв:</strong></p>
                                                                    <div
                                                                        className={`toggle-icon ${expandedComments[movie.id] ? 'open' : 'closed'}`}>
                                                                        {expandedComments[movie.id] ? '▼' : '▼'}
                                                                    </div>
                                                                </div>
                                                                {expandedComments[movie.id] && (
                                                                    <div className="comment-content">
                                                                        <p>{movie.comment}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {movie.watchedAt && (
                                                            <p className="watched-date">
                                                                Просмотрено: {formatDate(movie.watchedAt)}
                                                            </p>
                                                        )}
                                                        <div className="movie-actions">
                                                            <button
                                                                onClick={() => markAsWatched(movie.id)}
                                                                className="unwatched-btn"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button onClick={() => deleteMovie(movie.id)}
                                                                    className="delete-btn">
                                                                ❌
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="tv-seasons">
                                                        <div className="seasons-header"
                                                             onClick={() => toggleSeasonsList(movie.id)}>
                                                            <h4>Сезоны:</h4>
                                                            <div
                                                                className={`toggle-icon ${expandedTvShows[movie.id] ? 'open' : 'closed'}`}>
                                                                {expandedTvShows[movie.id] ? '▼' : '▼'}
                                                            </div>
                                                        </div>

                                                        {expandedTvShows[movie.id] && (
                                                            <ul className="seasons-list">
                                                                {movie.seasons?.map(season => (
                                                                    <li key={season.season_number}
                                                                        className="season-item">
                                                                        <div className="comment-header"
                                                                             onClick={() => toggleSeasonDetails(movie.id, season.season_number)}>
                                                                            <div style={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '10px',
                                                                                flex: 1
                                                                            }}>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        markSeasonAsWatched(movie.id, season.season_number, false);
                                                                                    }}
                                                                                    className={season.watched ? "unwatched-btn" : "watched-btn"}
                                                                                    style={{
                                                                                        width: '32px',
                                                                                        minWidth: '32px',
                                                                                        maxWidth: '32px',
                                                                                        padding: '6px',
                                                                                        flex: 'none'
                                                                                    }}
                                                                                    aria-label={season.watched ? 'Отменить' : 'Просмотрено'}
                                                                                >
                                                                                    ❌
                                                                                </button>

                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        markSeasonAsWatched(movie.id, season.season_number);
                                                                                    }}
                                                                                    className={season.watched ? "unwatched-btn" : "watched-btn"}
                                                                                    style={{
                                                                                        width: '32px',
                                                                                        minWidth: '32px',
                                                                                        maxWidth: '32px',
                                                                                        padding: '6px',
                                                                                        flex: 'none'
                                                                                    }}
                                                                                    aria-label={season.watched ? 'Отменить' : 'Просмотрено'}
                                                                                >
                                                                                    ✏️
                                                                                </button>
                                                                                <span>{season.name}</span>
                                                                            </div>

                                                                            <div
                                                                                className={`toggle-icon ${seasonDetailsExpanded[movie.id]?.[season.season_number] ? 'open' : 'closed'}`}>
                                                                                {seasonDetailsExpanded[movie.id]?.[season.season_number] ? '▼' : '▼'}
                                                                            </div>
                                                                        </div>

                                                                        {seasonDetailsExpanded[movie.id]?.[season.season_number] && season.watched && (
                                                                            <div
                                                                                className="season-info comment-content">
                                                                                {season.rating && <p>
                                                                                    <strong>Оценка:</strong> {season.rating}/10
                                                                                </p>}
                                                                                {season.watchedAt && <p>
                                                                                    <strong>Просмотрено:</strong> {formatDate(season.watchedAt)}
                                                                                </p>}
                                                                                {season.comment &&
                                                                                    <p className="season-comment">
                                                                                        <strong>Отзыв:</strong> {season.comment}
                                                                                    </p>}
                                                                            </div>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}

                                                        <button onClick={() => deleteMovie(movie.id)}
                                                                className="delete-btn">
                                                            Удалить
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {showWatched && watchedMovies.length === 0 && (
                                <p className="empty-group">Нет просмотренных фильмов или сериалов</p>
                            )}
                        </div>
                    </>
                )}
            </div>

            {selectedMovie && (
                <div className="modal-overlay">
                    <div className="comment-modal">
                        <h2>
                            {isSeason
                                ? `Отзыв о сезоне: ${selectedMovie.title} - ${selectedMovie.seasons?.find(s => s.season_number === selectedSeason)?.name}`
                                : `Отзыв о фильме: ${selectedMovie.title}`
                            }
                        </h2>

                        <div className="watched-date-selector">
                            <label htmlFor="watched-date">Дата просмотра:</label>
                            <input
                                type="date"
                                id="watched-date"
                                value={watchedDate}
                                onChange={(e) => setWatchedDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div className="rating-section">
                            <p>Ваша оценка:</p>
                            <StarRating
                                selectedRating={rating}
                                onRatingChange={setRating}
                            />
                        </div>

                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Напишите ваше мнение..."
                            rows={5}
                        />
                        <div className="modal-actions">
                            <button onClick={saveComment}>Сохранить</button>
                            <button onClick={() => {
                                setSelectedMovie(null);
                                setSelectedSeason(null);
                                setIsSeason(false);
                                setComment('');
                                setRating(0);
                                setWatchedDate(new Date().toISOString().split('T')[0]);
                            }}>Отмена</button>
                        </div>
                    </div>
                </div>
            )}

            {overviewMovie && (
                <div className="modal-overlay" onClick={() => setOverviewMovie(null)}>
                    <div className="comment-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{overviewMovie.title}</h2>
                        <p style={{whiteSpace: 'pre-line'}}>{overviewMovie.overview}</p>
                        <img
                            loading="lazy"
                            src={overviewMovie.poster_path ? `/tmdb-image/t/p/original${overviewMovie.poster_path}` : NO_POSTER}
                            alt={overviewMovie.title}
                        />

                    </div>
                </div>
            )}

        </div>
    );
};

export default WatchList;